'use client'

import { ORIGIN_STRING, STATION_ID_COOKIE } from "@/lib/constants"
import { ProfileData } from "@/lib/types/api/profile-data"
import Cookies from "js-cookie"
import { fetchViewingHistory } from "@/lib/content-services/queries"
import { CampaignCode, USER_ID_COOKIE } from '@/lib/constants';
import safeFetch from "@/lib/helpers/safe-fetch"
import {
  ClientSessionInvalid,
  GetFavoriteStationsServerError,
  GetProfileServerError,
  GetViewingHistoryServerError,
  GraphQLFetchError,
  NoUIDError
} from "@/lib/errors"
import { canAccessStorage } from "@/lib/helpers/is-storage-available"
import { humanizeDuration } from "@/lib/helpers/humanizeDuration"

export function getStationId() {
  return Cookies.get(STATION_ID_COOKIE) || window.SVP?.stationId
}

export function getUserId() {
  return Cookies.get(USER_ID_COOKIE)
}

/**
 * The signed-in user's profile, as the session sees it.
 *
 * Takes no uid: /sso/profile answers from the session cookie, and the passport
 * check below uses the pid out of its response. Reading the pid from pbs_uid
 * instead meant a valid session with an unreadable cookie threw NoUIDError,
 * which callers mistook for an invalid session. See sso-notes.txt Bug 1.
 *
 * The cookies are cleared by the server on the responses that prove the session
 * is finished, so nothing is cleared here.
 */
export async function getProfile(): Promise<ProfileData> {
  const response = await safeFetch(`${ORIGIN_STRING}/sso/profile`, {
    credentials: 'include'
  })

  if(response.status === 401 || response.status === 403 || response.status === 404) {
    throw new ClientSessionInvalid(`Invalid Session | Profile Response Status: ${response.status}`)
  }

  if(response.status >= 500) {
    throw new GetProfileServerError(`Server error: ${response.status}`, response.status)
  }

  const json = await response.json()

  // RWEB-9736
  // Override profile service passport flag with data directly from mvault.
  // If the passport check fails, keep whatever is_passport the profile service returned
  // rather than corrupting it with undefined (which causes VideoPlayer to reload without passport).
  try {
    const { passportEnabled } = await getPassportStatus(json.profile.pid)
    json.personal_data.is_passport = passportEnabled
  } catch (error) {
    console.warn('Passport status check failed, using profile service value:', error)
  }

  return json
}

export async function getPassportStatus(uid?: string) {
  const resolvedUid = uid || getUserId()

  if(!resolvedUid) {
    throw new NoUIDError('PBS UID Cookie is not set')
  }

  const response = await safeFetch(`/api/v1/membership/${resolvedUid}/passport/`)

  if (!response.ok) {
    throw new Error(`Passport status check failed: ${response.status}`)
  }

  const data = await response.json()

  if (typeof data.passportEnabled !== 'boolean') {
    throw new Error('Invalid passport status response')
  }

  return data
}

export async function getFavoriteStations(uid?: string) {
  const resolvedUid = uid || getUserId()

  if(!resolvedUid) {
    throw new NoUIDError('PBS UID Cookie is not set')
  }

  const response = await safeFetch('/api/profile/stations/')

  if(response.status >= 500) {
    throw new GetFavoriteStationsServerError(`Server error: ${response.status}`, response.status)
  }

  return await response.json()
}

export async function getViewingHistory(uid?: string) {
  const resolvedUid = uid || getUserId()

  if(!resolvedUid) {
    throw new NoUIDError('PBS UID Cookie is not set')
  }

  try {
    const viewingHistory = await fetchViewingHistory(resolvedUid)
    return viewingHistory
  } catch(error) {
    if (error instanceof GraphQLFetchError && error.statusCode >= 500) {
      throw new GetViewingHistoryServerError(`Server error: ${error.statusCode}`, error.statusCode)
    }

    throw error
  }
}

export async function setFavoriteStation(stationId: string, uid?: string) {
  const resolvedUid = uid || getUserId()

  if(!resolvedUid) {
    throw new NoUIDError('PBS UID Cookie is not set')
  }

  const response = await safeFetch('/api/profile/stations/', {
    method: 'PUT',
    body: JSON.stringify({ stationId })
  })
  return await response.json()
}

export async function putCampaignInfo(campaignInfoCode: CampaignCode) {
  const response = await safeFetch(
    `/api/profile/campaign-info/${campaignInfoCode}/`,
    { method: 'PUT' }
  )

  return response.ok
}


export const PROFILE_FILLED_AT_KEY = 'profile-filled-at'

export function setProfileFilledAt() {
  if (canAccessStorage('localStorage') && !window.localStorage.getItem(PROFILE_FILLED_AT_KEY)) {
    window.localStorage.setItem(PROFILE_FILLED_AT_KEY, Date.now().toString())
  }
}

export function getSessionDuration(): string {
  if (!canAccessStorage('localStorage')) return 'unknown'
  const filledAt = window.localStorage.getItem(PROFILE_FILLED_AT_KEY)
  if (!filledAt) return 'unknown'
  return humanizeDuration(Date.now() - parseInt(filledAt, 10))
}

export function clearProfileFilledAt() {
  if (canAccessStorage('localStorage')) {
    window.localStorage.removeItem(PROFILE_FILLED_AT_KEY)
  }
}
